import { useContext, useEffect } from "react";
import Box from "@mui/material/Box";
import { Grid } from "@mui/material";
import { Redirect, Route, Switch } from "react-router";

import ToolList from "tooldb/src/tools/ToolList";

import AppStateContext from "../contexts/AppStateContext.tsx";
import LoadingModal from "../components/Modals/LoadingModal.tsx";
import ThreeDModal from "components/Modals/ThreeDModal";
import MachineMaintenanceReminder from "components/Modals/MachineMaintenanceReminder";
import LogOutDialog from "../pages/authPages/LogOutDialog.tsx";
import Sidebar, { DRAWER_WIDTH } from "./Sidebar.tsx";
import { Finishes, Materials } from "util/MaterialUtils";

import Dashboard from "company_overview/dashboard/Dashboard";
import OrderManagementUI from "../pages/order_management/layout/OrderManagementUI.tsx";
import OrderView from "../pages/order_management/OrderView.tsx";
import DraftEdit from "../pages/order_management/DraftEdit.tsx";
import MachineManagementUI from "../pages/machine_management/layout/MachineManagementUI.tsx";
import StockManagementUI from "../pages/stock_management/layout/StockManagementUI.js";
import CustomerManagementUI from "../pages/customer_management/layout/CustomerManagementUI.tsx";
import CustomerBasketsUI from "pages/customer_management/customer_baskets/layout/CustomerBasketsUI";
import ProjectManagementUI from "../pages/project_management/layout/ProjectManagementUI.tsx";
import QuoteManagementUI from "pages/quote_management/layout/QuoteManagementUI";
import EmployeeManagementUI from "../pages/employee_management/layout/EmployeeManagementUI.tsx";
import PartnerManagementUI from "pages/partner_management/layout/PartnerManagementUI";
import OutsourcingManagmentUI from "pages/outsourcing_management/OutsourcingManagementUI";
import RequisitionListUI from "pages/outsourcing_management/requisitions/RequisitionListUI";
import SettingsUI from "pages/system_management/layout/SettingsUI";
import CamUI from "pages/tasks_management/camPage/CamUI";
import ReviewUI from "pages/tasks_management/reviewPage/ReviewUI";
import MaterialCutUI from "../pages/tasks_management/materialPage/MaterialCutUI.tsx";
import MillingUI from "../pages/tasks_management/millingPage/MillingUI.tsx";
import ShippingUI from "pages/tasks_management/shippingPage/ShippingUI.tsx";
import QualityControlUI from "pages/tasks_management/qcPage/QualityControlUI.tsx";
import ContentManagementUI from "pages/content_management/ContentManagementUI.tsx";
import CountriesManagementUI from "pages/content_management/CountriesManagementUI.tsx";
import CalendarUI from "pages/price_calculator/CalendarUI.tsx";
import ProductionPriorityUI from "pages/price_calculator/ProductionPriorityUI.tsx";
import PackingQueue from "pages/shipment/PackingQueue.tsx";
import PackingOrder from "pages/shipment/PackingOrder.tsx";
import ShipmentsList from "pages/shipment/ShipmentsList.tsx";
import LabelIssues from "pages/shipment/LabelIssues.tsx";
import UnmatchedLabels from "pages/shipment/UnmatchedLabels.tsx";
import ShippingPrices from "pages/shipment/ShippingPrices.tsx";
import ShipmentSettings from "pages/shipment/ShipmentSettings.tsx";

const DashboardLayout = () => {
  const { token, logUserOut, checkUserValid } = useContext(AppStateContext);
  const userRole = "admin";

  useEffect(() => {
    if (!token || token === "") {
      logUserOut();
      return;
    }
    checkUserValid();
  }, [token]);

  useEffect(() => {
    Materials.loadMaterials();
    Finishes.loadFinishes();
  }, []);

  return (
    <Box sx={{ display: "flex", width: "100%" }}>
      <ThreeDModal />
      <LoadingModal />
      <MachineMaintenanceReminder />

      <Sidebar />

      <Box
        component="main"
        sx={{ flexGrow: 1, p: 0, width: `calc(100% - ${DRAWER_WIDTH}px)` }}
      >
        <Grid container>
          <Switch>
            <Route path="/dashboard" component={Dashboard} />
            <Route path="/ordrestyring/:id" component={OrderView} />
            <Route path="/ordrestyring" component={OrderManagementUI} />
            <Route path="/maskine" component={MachineManagementUI} />
            <Route path="/production" component={ProjectManagementUI} />
            {/* EE-9: old Danish path kept working for existing bookmarks. */}
            <Redirect from="/produktion" to="/production" />
            <Route path="/review" component={ReviewUI} />
            <Route path="/cam" component={CamUI} />
            <Route path="/allProdction" component={MaterialCutUI} />
            <Route path="/milling" component={MillingUI} />
            <Route path="/qc" component={QualityControlUI} />
            <Route path="/shipping" component={ShippingUI} />
            <Route path="/shipment/packing/:orderId" component={PackingOrder} />
            <Route path="/shipment/packing" component={PackingQueue} />
            <Route path="/shipment/shipments" component={ShipmentsList} />
            <Route path="/shipment/label-issues" component={LabelIssues} />
            <Route path="/shipment/unmatched" component={UnmatchedLabels} />
            <Route path="/shipment/prices" component={ShippingPrices} />
            <Route path="/shipment/settings" component={ShipmentSettings} />
            {/* Both boards open their record in a dialog, and the id is in
                the path so the dialog can be linked to. The param is optional:
                one route, so moving between the board and a record on it does
                not remount the page. */}
            <Route path="/inquiries/:id?" component={OutsourcingManagmentUI} />
            {/* The Inquiries board was /outsourcing. Old links keep working. */}
            <Redirect from="/outsourcing/:id" to="/inquiries/:id" />
            <Redirect from="/outsourcing" to="/inquiries" />
            <Route
              path="/requisitions/:id?"
              component={RequisitionListUI}
            />
            <Route path="/threads" component={ContentManagementUI} />
            <Route path="/countries" component={CountriesManagementUI} />
            <Route path="/lagerstyring" component={StockManagementUI} />
            <Route path="/tools" component={ToolList} />
            <Route path="/quotes/:id" component={DraftEdit} />
            <Route path="/quotes" component={QuoteManagementUI} />
            <Route path="/baskets" component={CustomerBasketsUI} />
            <Route path="/kunder/:userId" component={CustomerManagementUI} />
            <Route path="/kunder" component={CustomerManagementUI} />
            <Route path="/settings" component={SettingsUI} />
            <Route path={"/calendar"} component={CalendarUI} />
            <Route
              path={"/production-priority"}
              component={ProductionPriorityUI}
            />
            <Route
              path="/brugere"
              component={
                userRole === "admin"
                  ? () => <EmployeeManagementUI />
                  : () => <div>Du har ikke adgang til bruger siden</div>
              }
            />
            <Route
              path="/partnere"
              component={
                userRole === "admin"
                  ? () => <PartnerManagementUI />
                  : () => <div>Du har ikke adgang til denne side</div>
              }
            />
            <Route path={"*"}>
              <Redirect to={"/dashboard"} />
            </Route>
          </Switch>
        </Grid>
      </Box>
      <LogOutDialog />
    </Box>
  );
};

export default DashboardLayout;
